<template>
  <ui-table :columns="columns" :rows="rows" :row-key="(row) => row.firm.id" :row-class="rowClass" :end-label="$t('actions')" @open="(row) => $emit('open', row.firm)">
    <template #cell-firm="{ row }">
      <div class="ui-who">
        <button v-if="row.childCount" type="button" class="df-expander" :aria-expanded="String(row.expanded)" :aria-label="`${$t('delivery_firms.branches', { count: row.childCount })}: ${firmName(row.firm)}`" @click.stop="$emit('toggle', row.firm)">
          <v-icon size="18">{{ row.expanded ? "mdi-chevron-down" : "mdi-chevron-right" }}</v-icon>
        </button>
        <span v-else-if="!row.depth" class="df-gap"></span>
        <ui-avatar :text="firmInitials(row.firm)" :tone="row.firm.isChild ? 'blue' : 'accent'" />
        <div :title="firmName(row.firm)">
          <b>{{ firmName(row.firm) }}</b>
          <small>
            #{{ row.firm.id }}
            <template v-if="row.firm.isChild"> · {{ $t("delivery_firms.branch_of", { name: toLatin(row.firm.parentName) }) }}</template>
            <template v-else-if="row.childCount"> · {{ $t("delivery_firms.branches", { count: row.childCount }) }}</template>
          </small>
        </div>
      </div>
    </template>

    <template #cell-status="{ row }">
      <ui-chip :tone="stateTone(row.firm)" dot>{{ stateText(row.firm) }}</ui-chip>
    </template>

    <template #cell-coop="{ row }">
      <div v-if="linkOf(row.firm)" class="df-coop">
        <span class="df-coop__tags">
          <ui-chip :tone="linkOf(row.firm).internal ? 'blue' : 'plain'">{{ $t(linkOf(row.firm).internal ? "delivery_firms.coop.internal" : "delivery_firms.coop.external") }}</ui-chip>
          <ui-chip v-if="isBillingMissing(row.firm)" tone="warn">{{ $t("delivery_firms.coop.unpaid") }}</ui-chip>
        </span>
        <span v-if="!isBillingMissing(row.firm) && linkOf(row.firm).payType" class="df-coop__pay">
          <b class="ui-num">{{ payAmountText(linkOf(row.firm).payType, linkOf(row.firm).paying, firmCurrency(row.firm)) }}</b>
          <small>{{ payTypeLabel(linkOf(row.firm).payType) }}</small>
        </span>
      </div>
      <span v-else-if="userType === 'admin' && row.firm.links.length" class="ui-muted">{{ $t("delivery_firms.coop.restaurants", { count: row.firm.links.length }) }}</span>
      <span v-else class="ui-muted">—</span>
    </template>

    <template #cell-city="{ row }">{{ cityLabel(row.firm) || "—" }}</template>

    <template #cell-contact="{ row }">
      <span class="nw">{{ row.firm.email || "—" }}</span>
      <small v-if="row.firm.phone" class="ui-num">{{ row.firm.phone }}</small>
    </template>

    <template #cell-users="{ row }">
      <router-link v-if="can(row.firm).users" class="ui-pill-link ui-num" :to="usersRoute(row.firm)" @click.native.stop>
        {{ row.firm.usersCount }}<v-icon size="14" color="currentColor">mdi-account-group-outline</v-icon>
      </router-link>
      <span v-else class="ui-muted">—</span>
    </template>

    <template #cell-orders="{ row }">
      <ui-switch v-if="can(row.firm).toggle" :value="Boolean(linkOf(row.firm).activeCompany)" :disabled="row.firm.active !== 1" :label="`${$t('delivery_firms.switch.label')}: ${firmName(row.firm)}`" :title="$t('delivery_firms.switch.hint')" @input="(on) => $emit('toggle-orders', row.firm, on)" />
      <span v-else class="ui-muted">—</span>
    </template>

    <template #end="{ row }">
      <ui-icon-button v-if="can(row.firm).cooperation" icon="mdi-cog-outline" size="sm" plain :label="`${$t('join_restoran')}: ${firmName(row.firm)}`" :title="$t('join_restoran')" @click.stop="$emit('cooperation', row.firm)" />
    </template>

    <template #card="{ row }">
      <ui-item :tone="cardTone(row.firm)" :branch="Boolean(row.depth)" :active="row.firm.id === activeId" @click="$emit('open', row.firm)">
        <span class="ui-who">
          <ui-avatar :text="firmInitials(row.firm)" :tone="row.firm.isChild ? 'blue' : 'accent'" />
          <span>
            <b>{{ firmName(row.firm) }}</b>
            <small>
              #{{ row.firm.id }}
              <template v-if="row.firm.isChild"> · {{ $t("delivery_firms.branch_of", { name: toLatin(row.firm.parentName) }) }}</template>
              <template v-else-if="row.childCount"> · {{ $t("delivery_firms.branches", { count: row.childCount }) }}</template>
            </small>
          </span>
        </span>
        <span class="ui-item__tags">
          <ui-chip :tone="stateTone(row.firm)" dot>{{ stateText(row.firm) }}</ui-chip>
          <ui-chip v-if="linkOf(row.firm)" :tone="linkOf(row.firm).internal ? 'blue' : 'plain'">{{ $t(linkOf(row.firm).internal ? "delivery_firms.coop.internal" : "delivery_firms.coop.external") }}</ui-chip>
          <ui-chip v-if="isBillingMissing(row.firm)" tone="warn">{{ $t("delivery_firms.coop.unpaid") }}</ui-chip>
        </span>
        <span v-if="cityLabel(row.firm) || (linkOf(row.firm) && linkOf(row.firm).payType)" class="ui-item__line">
          <span v-if="cityLabel(row.firm)">{{ $t("city") }} <b>{{ cityLabel(row.firm) }}</b></span>
          <span v-if="linkOf(row.firm) && linkOf(row.firm).payType">
            {{ $t("delivery_firms.col.coop") }}
            <b class="ui-num">{{ payAmountText(linkOf(row.firm).payType, linkOf(row.firm).paying, firmCurrency(row.firm)) }} · {{ payTypeLabel(linkOf(row.firm).payType).toLowerCase() }}</b>
          </span>
        </span>
      </ui-item>
      <div v-if="can(row.firm).toggle || can(row.firm).users || can(row.firm).cooperation" class="ui-item-actions" :class="{ 'is-branch': row.depth }">
        <router-link v-if="can(row.firm).users" class="ui-pill-link" :to="usersRoute(row.firm)">
          <v-icon size="16" color="currentColor">mdi-account-group-outline</v-icon>{{ $t("users") }} {{ row.firm.usersCount }}
        </router-link>
        <ui-icon-button v-if="can(row.firm).cooperation" icon="mdi-cog-outline" size="sm" plain :label="`${$t('join_restoran')}: ${firmName(row.firm)}`" :title="$t('join_restoran')" @click="$emit('cooperation', row.firm)" />
        <span v-if="can(row.firm).toggle" class="ui-item-actions__switch">
          {{ $t("delivery_firms.switch.label") }}
          <ui-switch :value="Boolean(linkOf(row.firm).activeCompany)" :disabled="row.firm.active !== 1" :label="`${$t('delivery_firms.switch.label')}: ${firmName(row.firm)}`" :title="$t('delivery_firms.switch.hint')" @input="(on) => $emit('toggle-orders', row.firm, on)" />
        </span>
      </div>
    </template>
  </ui-table>
</template>

<script>
import hasFirmLabels from "../hasFirmLabels";

/**
 * Lista firmi (UiTable): tabela na računaru, kartice na telefonu i užem ekranu. Matična firma ima dugme za otvaranje poslovnica;
 * red otvara karticu firme, a prekidač, veza "Korisnici" i zupčanik (saradnja, kod admina dijalog "Pridruži restoran") rade u samom redu.
 */
export default {
  name: "FirmList",
  mixins: [hasFirmLabels],
  inject: ["reportShell"],
  props: {
    rows: { type: Array, required: true },
    activeId: { type: Number, default: 0 },
  },
  computed: {
    columns() {
      return [
        { key: "firm", label: this.$t("delivery_firms.col.firm") },
        { key: "status", label: this.$t("status") },
        { key: "coop", label: this.$t("delivery_firms.col.coop") },
        { key: "city", label: this.$t("city"), hide: "sm", nowrap: true },
        { key: "contact", label: this.$t("delivery_firms.col.contact"), hide: "md" },
        { key: "users", label: this.$t("users") },
        { key: "orders", label: this.$t("delivery_firms.col.orders") },
      ];
    },
  },
  methods: {
    rowClass(row) {
      return {
        "is-on": row.firm.id === this.activeId,
        "is-off": this.stateOf(row.firm) !== "active" && !row.context,
        "is-context": row.context,
        "df-branch": Boolean(row.depth),
      };
    },
    /** boja lijeve trake kartice: pauzirana i firma bez dogovora o naplati upozoravaju, neaktivna je prigušena */
    cardTone(firm) {
      const state = this.stateOf(firm);
      if (state === "paused" || this.isBillingMissing(firm)) return "warn";
      return state === "inactive" || state === "deleted" ? "muted" : "";
    },
    usersRoute(firm) {
      return {
        name: "firma-dostava-id-korisnici",
        params: { id: firm.id, name: firm.name, parent_id: firm.parentId },
      };
    },
  },
};
</script>
